import { LocalStorage } from "./localStorage";
import {
  BetSlipBetInfo,
  BetSlipUpdatePayload,
  ByoSelectionDetail,
  BYOSelectionPayload,
  GtpTranslations,
  IframeSelectionDetail,
  IframeSelectionPayload,
  IframeSelections,
} from "../common/types";
import {
  getPopularBetBuilderBetListName,
  getPopularBetBuilderEventCategory,
} from "../common/utils";

interface StoredBetData {
  betUid: string; // "" for pending, "BYO_34" for active
  originalIframeEvent: IframeSelectionPayload;
  gtpTranslations: GtpTranslations;
  eventId: string;
  createdAt: number;
}

export interface PBBStorageData {
  bets: { [betKey: string]: StoredBetData };
}

export class StateManager {
  constructor(private readonly localStorage: LocalStorage) {}

  private getStorageData(): PBBStorageData {
    const data = this.localStorage.getPBBData();
    if (!data) {
      return { bets: {} };
    }
    return data;
  }

  private setStorageData(data: PBBStorageData): void {
    this.localStorage.setPBBData(data);
  }

  // ADD TO BETSLIP
  handleAddToBetslip(
    payload: IframeSelectionPayload,
    gtpTranslations: GtpTranslations,
  ): BYOSelectionPayload {
    this.storePendingBet(payload, gtpTranslations);
    return this.createBetslipPayload(payload, gtpTranslations);
  }

  private storePendingBet(
    payload: IframeSelectionPayload,
    gtpTranslations: GtpTranslations,
  ): void {
    const data = this.getStorageData();
    const eventId = payload.legacyEventId.replace("OB_EV", "");

    // Create unique key based on bet content hash to prevent duplicates
    const betHash = this.createBetHash(payload);

    data.bets[betHash] = {
      betUid: "", // Empty = pending
      originalIframeEvent: payload,
      gtpTranslations,
      eventId,
      createdAt: Date.now(),
    };

    this.setStorageData(data);
  }

  private createSelectionKey(selection: IframeSelectionDetail): string {
    return `${selection.marketId}_${selection.legacySelectionId}_${selection.handicap || "none"}`;
  }

  private createBetHash(payload: IframeSelectionPayload): string {
    // Create a unique hash based on all bet characteristics
    const selectionsStr = payload.byoSelectionDetails
      .map(this.createSelectionKey)
      .sort()
      .join("|");
    const { numerator, denominator, byoMarketId } = payload.byoPrice;
    const priceStr = `${numerator}_${denominator}_${byoMarketId}`;
    const eventStr = payload.legacyEventId;

    // Use a simple hash function to create a shorter but unique identifier
    const fullString = `${eventStr}_${selectionsStr}_${priceStr}`;
    let hash = 0;
    for (let i = 0; i < fullString.length; i++) {
      const char = fullString.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36); // Convert to base36 for shorter string
  }

  private createBetslipPayload(
    payload: IframeSelectionPayload,
    gtpTranslations: GtpTranslations,
  ): BYOSelectionPayload {
    const gtpEventId = gtpTranslations[payload.legacyEventId];
    if (!gtpEventId) {
      throw new Error(`Missing GTP event ID for ${payload.legacyEventId}`);
    }

    const byoSelectionDetails = this.translateSelections(
      payload.byoSelectionDetails,
      gtpTranslations,
    );

    const { numerator, denominator } = this.normalizeFraction(
      payload.byoPrice.numerator,
      payload.byoPrice.denominator,
    );

    return {
      byoEventId: gtpEventId,
      byoMarketId: payload.byoPrice.byoMarketId,
      byoSelectionDetails,
      byoPrice: {
        type: "FIXED_ODDS",
        numerator,
        denominator,
      },
      analytics: {
        ...payload.analytics,
        BetListName: [getPopularBetBuilderBetListName()],
        EventLabel: payload.analytics.accaName,
        EventCategory: getPopularBetBuilderEventCategory(),
      },
    };
  }

  private translateSelections(
    selections: IframeSelectionDetail[],
    gtpTranslations: GtpTranslations,
  ): Array<ByoSelectionDetail> {
    return selections.map((detail) => {
      const gtpSelectionId = gtpTranslations[detail.legacySelectionId];
      if (!gtpSelectionId) {
        throw new Error(
          `Missing GTP selection ID for ${detail.legacySelectionId}`,
        );
      }

      const result: ByoSelectionDetail = {
        selectionId: gtpSelectionId,
      };

      if (detail.handicap !== undefined) {
        result.handicap = detail.handicap;
      }

      return result;
    });
  }

  // Normalize numerator/denominator (remove decimals & reduce fraction)
  private normalizeFraction(numerator: number, denominator: number) {
    // handle decimals by scaling
    const numDecimals = Math.max(
      (numerator.toString().split(".")[1] || "").length,
      (denominator.toString().split(".")[1] || "").length,
    );

    const factor = Math.pow(10, numDecimals);
    const num = Math.round(numerator * factor);
    const den = Math.round(denominator * factor);

    // reduce by gcd
    const divisor = this.gcd(Math.abs(num), Math.abs(den));
    return {
      numerator: num / divisor,
      denominator: den / divisor,
    };
  }

  private gcd(a: number, b: number): number {
    return b === 0 ? a : this.gcd(b, a % b);
  }

  // BETSLIP UPDATE
  handleOnBetslipUpdate(payload: BetSlipUpdatePayload): IframeSelections {
    const data = this.getStorageData();

    this.removeExpiredBets(data); // remove expired bets
    this.removeObsoleteBets(data, payload); // remove bets which are no longer in betSlip
    this.promoteNewBets(data, payload); // add betUid to bets from iframe

    this.setStorageData(data);

    return this.generateIframeSelections(data);
  }

  private removeExpiredBets(data: PBBStorageData): void {
    const fiveDaysAgo = Date.now() - 1000 * 60 * 60 * 24 * 5;
    Object.entries(data.bets).forEach(([betKey, bet]) => {
      if (bet.createdAt && bet.createdAt < fiveDaysAgo) {
        delete data.bets[betKey];
      }
    });
  }

  private removeObsoleteBets(
    data: PBBStorageData,
    payload: BetSlipUpdatePayload,
  ): void {
    const currentBetUids = new Set(Object.keys(payload));
    const activeBets = Object.entries(data.bets).filter(
      ([, bet]) => bet.betUid !== "",
    );

    const storedBetUids = new Set(activeBets.map(([, bet]) => bet.betUid));
    const removedBetUids = Array.from(storedBetUids).filter(
      (betUid) => !currentBetUids.has(betUid),
    );
    const pendingBets = Object.entries(data.bets).filter(
      ([, bet]) => bet.betUid === "",
    );

    // Remove bets where betSlip BYO_ID is missing in storage
    removedBetUids.forEach((betUid) => {
      const betKeyToRemove = Object.keys(data.bets).find(
        (key) => data.bets[key]?.betUid === betUid,
      );
      if (betKeyToRemove) {
        delete data.bets[betKeyToRemove];
      }
    });

    // Remove bets where BYO_ID is matching but the selections are not
    activeBets.forEach(([betKey, storedBet]) => {
      const betUid = storedBet.betUid;
      const currentBetInfo = payload[betUid];

      if (
        currentBetInfo &&
        !this.isBetContentMatching(storedBet, currentBetInfo)
      ) {
        delete data.bets[betKey];
      }
    });

    // Remove bets where BYO_ID is missing but the selections are not
    pendingBets.forEach(([betKey, storedBet]) => {
      const hasMatchingCurrentBet = Object.values(payload).some(
        (currentBetInfo) =>
          this.isBetContentMatching(storedBet, currentBetInfo),
      );

      if (!hasMatchingCurrentBet) {
        delete data.bets[betKey];
      }
    });
  }

  // Normalize handicap values for comparison
  private normalizeHandicap(
    handicap: number | undefined | null,
  ): number | null {
    return handicap ?? null;
  }

  private isBetContentMatching(
    storedBet: StoredBetData,
    currentBetInfo: BetSlipBetInfo,
  ): boolean {
    if (storedBet.eventId !== currentBetInfo.eventId) {
      return false;
    }

    const storedGtpSelections =
      storedBet.originalIframeEvent.byoSelectionDetails
        .map((detail) => ({
          selectionId: storedBet.gtpTranslations[detail.legacySelectionId],
          handicap: detail.handicap,
        }))
        .filter((selection) => selection.selectionId); // Filter out missing translations

    const currentSelections = currentBetInfo.selectionDetails.map(
      (detail: any) => ({
        selectionId: detail.selectionId,
        handicap: detail.handicap,
      }),
    );

    if (storedGtpSelections.length !== currentSelections.length) {
      return false;
    }

    // Check if every stored selection has a matching current selection
    return storedGtpSelections.every((storedSelection) => {
      return currentSelections.some((currentSelection) => {
        return (
          storedSelection.selectionId === currentSelection.selectionId &&
          this.normalizeHandicap(storedSelection.handicap) ===
            this.normalizeHandicap(currentSelection.handicap)
        );
      });
    });
  }

  private promoteNewBets(
    data: PBBStorageData,
    payload: BetSlipUpdatePayload,
  ): void {
    const currentBetUids = new Set(Object.keys(payload));

    const activeBets = Object.values(data.bets).filter(
      (bet) => bet.betUid !== "",
    );
    const storedBetUids = new Set(activeBets.map((bet) => bet.betUid));
    const newBetUids = Array.from(currentBetUids).filter(
      (betUid) => !storedBetUids.has(betUid),
    );

    newBetUids.forEach((betUid) => {
      const betInfo = payload[betUid];
      const pendingBetEntry = this.findMatchingPendingBet(data, betInfo);

      if (pendingBetEntry) {
        const [pendingKey, pendingBet] = pendingBetEntry;
        data.bets[pendingKey] = {
          ...pendingBet,
          betUid, // Set the real BYO_XX betUid
        };
      }
    });
  }

  private findMatchingPendingBet(
    data: PBBStorageData,
    betInfo: BetSlipBetInfo,
  ): [string, StoredBetData] | undefined {
    const eventId = betInfo?.eventId;

    return Object.entries(data.bets).find(([, bet]) => {
      if (bet.betUid !== "" || bet.eventId !== eventId) return false;

      const pendingGtpSelectionIds = bet.originalIframeEvent.byoSelectionDetails
        .map((detail) => bet.gtpTranslations[detail.legacySelectionId])
        .filter(Boolean)
        .sort();

      const betslipSelectionIds = betInfo?.selectionDetails
        .map((detail: any) => detail.selectionId)
        .sort();

      return (
        pendingGtpSelectionIds.length === betslipSelectionIds?.length &&
        pendingGtpSelectionIds.every(
          (id, index) => id === betslipSelectionIds[index],
        )
      );
    });
  }

  private generateIframeSelections(data: PBBStorageData): IframeSelections {
    const allActiveSelections: IframeSelectionDetail[] = [];

    Object.values(data.bets).forEach((bet) => {
      if (bet.betUid !== "") {
        // Only active bets
        bet.originalIframeEvent.byoSelectionDetails.forEach((selection) => {
          allActiveSelections.push(selection);
        });
      }
    });

    const uniqueSelections = this.deduplicateSelections(allActiveSelections);

    return {
      byoSelectionDetails: uniqueSelections,
    };
  }

  private deduplicateSelections(
    selections: IframeSelectionDetail[],
  ): IframeSelectionDetail[] {
    const uniqueMap = new Map<string, IframeSelectionDetail>();

    selections.forEach((selection) => {
      const key = this.createSelectionKey(selection);
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, selection);
      }
    });

    return Array.from(uniqueMap.values());
  }

  // REMOVE FROM IFRAME
  handleRemoveFromIframe(payload: IframeSelectionPayload): string {
    const data = this.getStorageData();
    const betUidsToRemove = this.removeBetMatchingPayload(data, payload);
    this.setStorageData(data);

    return betUidsToRemove;
  }

  private removeBetMatchingPayload(
    data: PBBStorageData,
    payload: IframeSelectionPayload,
  ): string {
    let betUidToRemove = "";

    Object.entries(data.bets).forEach(([betKey, betData]) => {
      if (this.isSameBet(betData.originalIframeEvent, payload)) {
        if (betData.betUid !== "") {
          // Only remove active bets
          betUidToRemove = betData.betUid;
        }
        delete data.bets[betKey];
      }
    });

    return betUidToRemove;
  }

  private isSameBet(
    stored: IframeSelectionPayload,
    incoming: IframeSelectionPayload,
  ): boolean {
    if (stored.legacyEventId !== incoming.legacyEventId) return false;
    if (stored.byoPrice.numerator !== incoming.byoPrice.numerator) return false;
    if (stored.byoPrice.denominator !== incoming.byoPrice.denominator)
      return false;
    if (
      stored.byoSelectionDetails.length !== incoming.byoSelectionDetails.length
    )
      return false;

    return stored.byoSelectionDetails.every((storedSelection) =>
      incoming.byoSelectionDetails.some(
        (incomingSelection) =>
          storedSelection.marketId === incomingSelection.marketId &&
          storedSelection.legacySelectionId ===
            incomingSelection.legacySelectionId &&
          storedSelection.handicap === incomingSelection.handicap,
      ),
    );
  }
}
